import React, { type FC } from 'react';
import { CheckCircle2, Calendar } from 'lucide-react';
import { CardShell } from './card-shell';
import { ApprovalBanner } from './approval-banner';
import type { ToolCardProps } from './types';
import { safeGet } from './types';

export const MessageConfirmCard: FC<ToolCardProps> = ({
  toolName,
  args,
  result,
  stage,
  approvalId,
}) => {
  const isInterview = toolName === 'messageScheduleInterview';
  const messageContent =
    safeGet<string>(result, 'message') ??
    safeGet<string>(args, 'message') ??
    safeGet<string>(result, 'content') ??
    safeGet<string>(args, 'content') ??
    '';

  const datetime =
    safeGet<string>(args, 'datetime') ??
    safeGet<string>(args, 'date') ??
    safeGet<string>(result, 'datetime');
  const interviewType =
    safeGet<string>(args, 'type') ??
    safeGet<string>(args, 'interviewType') ??
    safeGet<string>(result, 'type');

  // Clamp message to first 2 lines worth of text
  const previewLines = messageContent.split('\n').slice(0, 2).join('\n');

  if (stage === 'pending_approval') {
    return (
      <CardShell>
        <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: 'var(--text-primary)',
            }}
          >
            {isInterview ? 'Schedule this interview?' : 'Send this message?'}
          </div>

          {previewLines && (
            <div
              style={{
                fontSize: 13,
                color: 'var(--text-secondary)',
                lineHeight: '18px',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                whiteSpace: 'pre-wrap',
              }}
            >
              {previewLines}
            </div>
          )}

          {isInterview && datetime && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Calendar
                size={14}
                style={{ color: 'var(--text-tertiary)', flexShrink: 0 }}
              />
              <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                {datetime}
              </span>
              {interviewType && (
                <span
                  style={{
                    fontSize: 11,
                    color: 'var(--text-secondary)',
                    background: 'rgba(0,0,0,0.04)',
                    borderRadius: 9999,
                    padding: '2px 8px',
                    lineHeight: '16px',
                  }}
                >
                  {interviewType}
                </span>
              )}
            </div>
          )}
        </div>
        {approvalId && (
          <ApprovalBanner
            approvalId={approvalId}
            toolName={toolName}
            description={
              isInterview
                ? 'Confirm interview scheduling?'
                : 'Confirm sending this message?'
            }
          />
        )}
      </CardShell>
    );
  }

  // completed / other stages
  return (
    <CardShell>
      <div
        style={{
          padding: 18,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <CheckCircle2
            size={16}
            style={{ color: 'var(--text-secondary)', flexShrink: 0 }}
          />
          <span
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: 'var(--text-primary)',
            }}
          >
            {isInterview ? 'Interview scheduled' : 'Message sent'}
          </span>
        </div>

        {previewLines && (
          <div
            style={{
              fontSize: 13,
              color: 'var(--text-secondary)',
              lineHeight: '18px',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              whiteSpace: 'pre-wrap',
              paddingLeft: 24,
            }}
          >
            {previewLines}
          </div>
        )}

        {isInterview && datetime && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              paddingLeft: 24,
            }}
          >
            <Calendar
              size={14}
              style={{ color: 'var(--text-tertiary)', flexShrink: 0 }}
            />
            <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
              {datetime}
            </span>
            {interviewType && (
              <span
                style={{
                  fontSize: 11,
                  color: 'var(--text-secondary)',
                  background: 'rgba(0,0,0,0.04)',
                  borderRadius: 9999,
                  padding: '2px 8px',
                  lineHeight: '16px',
                }}
              >
                {interviewType}
              </span>
            )}
          </div>
        )}
      </div>
    </CardShell>
  );
};
